
import React, { useEffect, useState } from 'react';
import { SEOHead } from '@/components/SEOHead';
import { AdminDashboard } from '@/components/AdminDashboard';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, RefreshCw, AlertCircle } from 'lucide-react';

const Admin = () => {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adminCheckError, setAdminCheckError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAdminAccess = async () => {
      if (!authLoading && !user) {
        navigate('/');
        return;
      }

      if (user) {
        try {
          const { data: isAdminResult, error: rpcError } = await supabase.rpc('is_admin', { user_id: user.id });

          if (rpcError) {
            setAdminCheckError(`Error: ${rpcError.message}`);
            setIsAdmin(false);
          } else {
            setIsAdmin(isAdminResult || false);
            if (!isAdminResult) {
              navigate('/dashboard');
              return;
            }
          }
        } catch (error) {
          setAdminCheckError(`Unexpected error: ${error}`);
          setIsAdmin(false);
          navigate('/dashboard');
          return;
        }
      }

      setLoading(false);
    };

    checkAdminAccess();
  }, [user, authLoading, navigate]);

  const handleRefreshCheck = async () => {
    setLoading(true);
    setAdminCheckError(null);

    if (user) {
      try {
        const { data, error } = await supabase.rpc('is_admin', { user_id: user.id });
        if (error) {
          setAdminCheckError(`Error: ${error.message}`);
          setIsAdmin(false);
        } else {
          setIsAdmin(data || false);
        }
      } catch (error) {
        setAdminCheckError(`Unexpected error: ${error}`);
        setIsAdmin(false);
      }
    }
    setLoading(false);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <SEOHead
          title="Admin | Milky Way Planner"
          description="Milky Way Planner administration. This page checks admin access and is not one of the public astrophotography planning tools."
          noindex
        />
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <h1 className="text-2xl font-semibold text-foreground">Admin</h1>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-8 max-w-md">
          <CardContent className="flex flex-col items-center gap-4 text-center">
            <Shield className="w-10 h-10 text-muted-foreground" />
            <h1 className="text-xl font-semibold text-foreground">Access Denied</h1>
            <p className="text-muted-foreground">You don't have admin access.</p>

            {adminCheckError && (
              <div className="mt-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg w-full">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0" />
                  <p className="text-sm text-destructive">{adminCheckError}</p>
                </div>
              </div>
            )}

            <Button onClick={handleRefreshCheck} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="Admin | Milky Way Planner"
        description="Milky Way Planner administration. This page checks admin access and is not one of the public astrophotography planning tools."
        noindex
      />
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <h1 className="text-2xl font-semibold text-foreground mb-8">Admin</h1>
          <AdminDashboard />
        </div>
      </div>
    </>
  );
};

export default Admin;
